import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { apiRequest } from '../../utils/apiClient';
import { useDeveloper } from '../../contexts/DeveloperContext';
import { hasPermission } from '../../utils/permissions';

export default function DeveloperSettingsPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const t = (en: string, ar: string) => (isRTL ? ar : en);
  const { developer, myRole, refetch } = useDeveloper();
  const canEdit = hasPermission(myRole, 'ADMIN');

  const [form, setForm] = useState({
    name: '', nameAr: '', description: '', descriptionAr: '',
    phone: '', email: '', website: '', address: '', headquartersCity: '', logoUrl: '', coverUrl: '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (developer) {
      setForm({
        name: developer.name || '', nameAr: developer.nameAr || '',
        description: developer.description || '', descriptionAr: developer.descriptionAr || '',
        phone: developer.phone || '', email: developer.email || '', website: developer.website || '',
        address: developer.address || '', headquartersCity: developer.headquartersCity || '',
        logoUrl: developer.logoUrl || '', coverUrl: developer.coverUrl || '',
      });
    }
  }, [developer]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await apiRequest('/developers/my/profile', { method: 'PATCH', body: form });
      setMsg(t('Saved.', 'تم الحفظ.'));
      await refetch();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : t('Failed to save.', 'تعذّر الحفظ.'));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#004D40] disabled:bg-slate-50';
  const field = (label: string, k: keyof typeof form, type = 'text', area = false) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>
      {area ? (
        <textarea className={`${inputCls} min-h-[80px] resize-y`} value={form[k]} onChange={set(k)} disabled={!canEdit} />
      ) : (
        <input type={type} className={inputCls} value={form[k]} onChange={set(k)} disabled={!canEdit} />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className={`mb-6 text-xl font-bold text-[var(--pv-color-primary)] ${isRTL ? 'text-right' : ''}`}>{t('Developer settings', 'إعدادات المطوّر')}</h1>
        <form onSubmit={save} className={`space-y-4 rounded-2xl border border-slate-200/80 bg-white p-6 ${isRTL ? 'text-right' : ''}`}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field(t('Name (EN)', 'الاسم (إنجليزي)'), 'name')}
            {field(t('Name (AR)', 'الاسم (عربي)'), 'nameAr')}
            {field(t('Phone', 'الهاتف'), 'phone')}
            {field(t('Email', 'البريد الإلكتروني'), 'email', 'email')}
            {field(t('Website', 'الموقع'), 'website')}
            {field(t('HQ city', 'مدينة المقر'), 'headquartersCity')}
            {field(t('Logo URL', 'رابط الشعار'), 'logoUrl')}
            {field(t('Cover URL', 'رابط الغلاف'), 'coverUrl')}
          </div>
          {field(t('Address', 'العنوان'), 'address')}
          {field(t('Description (EN)', 'الوصف (إنجليزي)'), 'description', 'text', true)}
          {field(t('Description (AR)', 'الوصف (عربي)'), 'descriptionAr', 'text', true)}

          {canEdit ? (
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button type="submit" disabled={busy} className="rounded-full bg-[#004D40] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {busy ? t('Saving…', 'جارٍ الحفظ…') : t('Save changes', 'حفظ التغييرات')}
              </button>
              {msg && <span className="text-sm text-slate-500">{msg}</span>}
            </div>
          ) : (
            <p className="text-sm text-slate-400">{t('You do not have permission to edit these settings.', 'ليس لديك صلاحية تعديل هذه الإعدادات.')}</p>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
}
